import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  HistoryOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table/interface";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import VuiButton from "../../../components/button";
import VuiFormItemView from "../../../components/form-item-view";
import VuiPageTitle from "../../../components/page-title";
import { ENV, INVOICE_STATUS_COLOR } from "../../../constants";
import { useBreadcrumb } from "../../../context/breadcrumb";
import { BreadcrumbActionType } from "../../../context/breadcrumb/reducer";
import { useFormInvoice } from "../hooks/use-form-invoice";
import { useGetInvoice } from "../hooks/use-get-invoice";
import {
  InvoicePaidOffFormData,
  InvoicePaidOffPayloadType,
  InvoiceRecurringSettingFormData,
  InvoiceRecurringSettingPayloadType,
  InvoiceUploadFormData,
  InvoiceUploadPayloadType,
} from "./interface";
import ModalUpload from "../components/modal-upload";
import {
  InvoiceEmailLog,
  InvoiceNotifcationHistoryLog,
  InvoicePayment,
  InvoiceProduct,
  InvoiceWhatsappLog,
} from "../../../models/invoice";
import VuiNumberFormat from "../../../components/number-format";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import VuiAccessible from "../../../components/accessible";
import {
  handleBackendError,
  inputNumberThousandSeparator,
  openNotification,
  snakeCaseToTitleCase,
  uppercaseToTitleCase,
} from "../../../utils/helpers";
import VuiDatePicker from "../../../components/vui-date-picker";
import VuiSelect from "../../../components/select";
import ChartOfAccountRepository from "../../../repositories/chart-of-account-repository";
import VuiUploadImage from "../../../components/upload-image";
import { Option } from "antd/lib/mentions";
import { useGetFirstData } from "../hooks/use-get-first-data";
import GoogleIcon from "../../../assets/icons/google-icon";
import InvoiceRepository from "../../../repositories/invoice-repository";
import { AxiosError } from "axios";
import { useCopyToClipboard } from "usehooks-ts";

const { Text, Title } = Typography;

type ApprovalType = "approve" | "reject";

const InvoiceDetailModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadDetailData, isOnFetchingDetailData, detailData } =
    useGetInvoice();
  const {
    data: defaultPay,
    isOnFetchingData: isOnFetchingDataDefaultPay,
    loadData: loadDefaultPay,
  } = useGetFirstData(ChartOfAccountRepository);
  const [, copy] = useCopyToClipboard();
  const title = `${t("common.text.invoice")} | `;
  const {
    uploadLoading,
    onUpload,
    downloadLoading,
    onDownload,
    sendLoading,
    onSend,
    paidOffLoading,
    onSubmitPaidOff,
    isApprovalSubmitting,
    onApproval,
    onDelete,
    deleteLoading,
    onSendReminder,
    sendReminderLoading,
    onUpdateRecurringSetting,
    onDeleteRecurringSetting,
    recurringSettingLoading,
  } = useFormInvoice();
  const { dispatch } = useBreadcrumb();
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showSendForm, setShowSendForm] = useState(false);
  const [showPaidOffConfirmation, setShowPaidOffConfirmation] = useState(false);
  const [showApprovalConfirmation, setShowApprovalConfirmation] =
    useState<ApprovalType | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteRecurringModal, setShowDeleteRecurringModal] =
    useState<boolean>(false);
  const [showRecurringSettingModal, setShowRecurringSettingModal] =
    useState<boolean>(false);
  const [withholdingType, setWithholdingType] = useState("PERCENTAGE");
  const [withholdingRealAmount, setWithholdingRealAmount] = useState<number>(0);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState<boolean>(false);
  const [showRejectPaymentModal, setShowRejectPaymentModal] = useState<boolean>(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [confirmPaymentLoading, setConfirmPaymentLoading] = useState<boolean>(false);
  const [rejectPaymentLoading, setRejectPaymentLoading] = useState<boolean>(false);
  const [showSendReminderForm, setShowSendReminderForm] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [form] = Form.useForm();
  const withHoldingAccountSelected = Form.useWatch("withholding_coa_id", form);
  const canSendReminder =
    (detailData?.contact_emails?.length ?? 0) > 0 ||
    (detailData?.contact_whatsapp?.length ?? 0) > 0;

  const repeatUntil = Form.useWatch("end_repeat", form);
  const hasRecurringSetting = !!detailData?.recurring_setting;
  const googleWorkspaceGenerated = !!detailData?.gw_customer_id;

  const hasPPN = detailData?.is_ppn;
  const invoiceProducts = detailData?.invoice_products;
  const subtotal =
    invoiceProducts
      ?.map((item) => {
        const price = item?.price || 0;
        const quantity = item?.quantity || 0;
        if (!item?.product_id && !item?.gw_product_id) {
          return 0;
        }
        return Number(price) * quantity;
      })
      .reduce((total: number, num: number) => total + num) || 0;

  const ppn = hasPPN ? (11 / 100) * subtotal : 0;

  const total = subtotal + ppn;

  const invoiceEmails = useMemo(() => {
    return detailData?.invoice_emails?.map((item) => item.email).join(", ");
  }, [detailData]);

  const haveEmailContact = useMemo(() => {
    return (detailData?.contact_emails?.length ?? 0) > 0;
  }, [detailData]);

  const haveWhatsappContact = useMemo(() => {
    return (detailData?.contact_whatsapp?.length ?? 0) > 0;
  }, [detailData]);

  const invoiceContactEmails = useMemo(() => {
    return detailData?.contact_emails
      ?.map((contact) => contact.email)
      .join(", ");
  }, [detailData]);

  const invoiceContactCcEmails = useMemo(() => {
    const ccEmails = detailData?.contact_cc_emails;
    if (Array.isArray(ccEmails)) {
      return ccEmails
        .map((contact: any) =>
          typeof contact === "string" ? contact : contact?.email
        )
        .filter(Boolean)
        .join(", ");
    }
    return typeof ccEmails === "string" ? ccEmails : "";
  }, [detailData]);

  const invoiceContactWhatsapp = useMemo(() => {
    return detailData?.contact_whatsapp
      ?.map((contact) => contact.phone)
      .join(", ");
  }, [detailData]);

  useEffect(() => {
    if (defaultPay) {
      form.setFieldsValue({
        pay_coa_id: {
          label: defaultPay?.name,
          value: defaultPay?.id,
        },
      });
    }
  }, [defaultPay]);

  const invoiceProductColumns: ColumnsType<InvoiceProduct> = [
    {
      title: t("common.text.product"),
      dataIndex: "product_id",
      key: "product_id",
      render: (text, record) => (
        <Text>{record.product?.name || record?.gw_product_name}</Text>
      ),
    },
    {
      title: t("common.text.description"),
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {text}
        </Text>
      ),
    },
    {
      title: t("common.text.qty"),
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: t("common.text.units"),
      dataIndex: "unit_id",
      key: "unit_id",
      render: (text, record) => {
        let name = record.unit?.name;
        if (!name) {
          name = record?.gw_product_unit === "LICENSE" ? "License" : "Seat";
        }
        return <Text>{name}</Text>;
      },
    },
    {
      title: `${t("common.text.unitPrice")} (Rp)`,
      dataIndex: "price",
      key: "price",
      render: (text) => <VuiNumberFormat value={text} />,
    },
    {
      title: `${t("common.text.amount")} (Rp)`,
      key: "price",
      render: (text, record) => (
        <VuiNumberFormat value={record?.quantity * record?.price} />
      ),
    },
  ];

  const invoicePaymentColumns: ColumnsType<InvoicePayment> = [
    {
      title: t("common.text.paymentDate"),
      dataIndex: "payment_at",
      key: "payment_at",
      render: (text) => moment(text).format("DD MMM YYYY"),
    },
    {
      title: t("common.text.payToAccount"),
      dataIndex: "pay_coa",
      key: "pay_coa",
      render: (text, record) => record.pay_coa?.name,
    },
    {
      title: t("common.text.paymentMethod"),
      dataIndex: "payment_method",
      key: "payment_method",
      render: (text) => _.startCase(_.toLower(text.replace("_", " "))),
    },
    {
      title: t("common.text.withholdingAccount"),
      dataIndex: "withholding_coa",
      key: "withholding_coa",
      render: (text, record) => record.withholding_coa?.name,
    },
    {
      title: `${t("common.text.withholdingAmount")} (Rp)`,
      dataIndex: "withholding_amount",
      key: "withholding_amount",
      render: (text) => <VuiNumberFormat value={text} />,
    },
    {
      title: t("common.text.attachment"),
      key: "attachment",
      render: (text, record) =>
        record.attachment && (
          <a href={record.attachment.url} target="_blank" rel="noopener noreferrer">
            {record.attachment.name}
          </a>
        ),
    },
    {
      title: t("common.text.status"),
      key: "status",
      render: (text, record) => snakeCaseToTitleCase(record.status),
    },
    {
      title: t("common.text.action"),
      key: "action",
      render: (_, record) => {
        const waiting = record.status === "confirmation";
        if (!waiting) return null;

        return (
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setSelectedPaymentId(record.id as number);
                setShowConfirmPaymentModal(true);
              }}
            >
              Confirm
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                setSelectedPaymentId(record.id as number);
                setShowRejectPaymentModal(true);
              }}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  const notificationHistoryColumns: ColumnsType<InvoiceNotifcationHistoryLog> = [
    {
      title: t("common.text.channel"),
      dataIndex: "channel",
      key: "channel",
      render: (text) => text,
    },
    {
      title: t("common.text.from"),
      dataIndex: "from_address",
      key: "from_address",
      render: (text) => text || "-",
    },
    {
      title: t("common.text.to"),
      dataIndex: "to_address",
      key: "to_address",
      render: (text) => text,
    },
    {
      title: t("common.text.action"),
      dataIndex: "action",
      key: "action",
      render: (text) => snakeCaseToTitleCase(text),
    },
    {
      title: t("common.text.sender"),
      dataIndex: "sender",
      key: "sender",
      render: (text, record) => record.sender?.name || "",
    },
    {
      title: t("common.text.sendDate"),
      dataIndex: "created_at",
      key: "created_at",
      render: (text) =>
        text ? moment(text).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const notificationHistoryData = useMemo(() => {
    const emailLogs = (detailData?.email_logs || []).map(log => ({
      id: log.id,
      channel: 'Email' as const,
      from_address: log.from_email,
      to_address: log.to_email,
      action: log.action,
      sender: log.sender,
      created_at: log.created_at,
    }));
    const whatsappLogs = (detailData?.whatsapp_logs || []).map(log => ({
      id: log.id,
      channel: 'WhatsApp' as const,
      from_address: undefined,
      to_address: log.phone,
      action: log.action,
      sender: log.sender,
      created_at: log.created_at,
    }));
    return [...emailLogs, ...whatsappLogs].sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return (b.id as number) - (a.id as number);
    });
  }, [detailData?.email_logs, detailData?.whatsapp_logs]);

  useEffect(() => {
    dispatch({
      type: BreadcrumbActionType.Update,
      payload: [
        {
          link: "/",
          title: t("common.text.home"),
        },
        {
          link: "/invoice",
          title: t("common.text.invoice"),
        },
        {
          link: `/invoice/${id}`,
          title: detailData?.name,
          isDisabled: true,
        },
      ],
    });
  }, [dispatch, t, detailData?.name, id]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadData(id);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (detailData) {
      form.setFieldsValue({
        amount: total,
        receive_amount: total,
      });
    }
  }, [detailData, form, total]);

  useEffect(() => {
    if (detailData?.recurring_setting) {
      form.setFieldsValue({
        repeat_number: detailData?.recurring_setting?.number,
        repeat_unit: detailData?.recurring_setting?.unit,
        end_repeat: detailData?.recurring_setting?.end_repeat,
        end_after: detailData?.recurring_setting?.end_after,
        end_on: detailData?.recurring_setting?.end_on
          ? moment(detailData?.recurring_setting?.end_on, "YYYY-MM-DD")
          : null,
      });
    }
  }, [detailData, form]);

  const handleEdit = () => {
    navigate(`/invoice/${id}/edit`);
  };

  const loadData = useCallback(
    (id: string) =>
      loadDetailData(id, {
        with: [
          "creator",
          "customer",
          "projectManager",
          "project",
          "status",
          "invoiceProducts.product",
          "invoiceProducts.unit",
          "sentType",
          "invoiceEmails",
          "invoicePayments.attachment",
          "invoicePayments.payCoa",
          "invoicePayments.withholdingCoa",
          "contactEmails",
          "contactCcEmails",
          "contactWhatsapp",
          "template",
          "customer.type",
          "recurringSetting.parentInvoice",
          "emailLogs.sender",
          "whatsappLogs.sender",
        ],
      }),
    [loadDetailData]
  );

  const handleUpload = async (formData: InvoiceUploadFormData) => {
    const normalizeData: InvoiceUploadPayloadType = {
      document_id: formData?.document_id[0]?.id,
    };

    if (id) {
      await onUpload(id, normalizeData);
      setShowUploadForm(false);
      await loadData(id);
    }
  };

  const handleDownload = () => {
    if (id) {
      onDownload(id, detailData?.name || "");
    }
  };

  const handleSend = async () => {
    if (id) {
      await onSend(id);
      setShowSendForm(false);
      await loadData(id);
    }
  };

  const handleSendReminder = async () => {
    if (id) {
      await onSendReminder(id);
      setShowSendReminderForm(false);
      await loadData(id);
    }
  };

  const handleSubmitPaidOff = useCallback(
    async (data: InvoicePaidOffFormData) => {
      if (id) {
        const normalizeData: InvoicePaidOffPayloadType = {
          date: moment(data.date).format("YYYY-MM-DD"),
          pay_coa_id: data.pay_coa_id.value as number,
          payment_method: data.payment_method,
          withholding_coa_id: data?.withholding_coa_id?.value as number,
          withholding_amount:
            withholdingType === "PERCENTAGE" && data.withholding_amount
              ? (data.withholding_amount / 100) * total
              : data.withholding_amount,
          attachment_id:
            data.attachment_id && data.attachment_id.length > 0
              ? data.attachment_id[0].id
              : undefined,
        };

        await onSubmitPaidOff(normalizeData, id);

        setShowPaidOffConfirmation(false);
        await loadData(id);
      }
    },
    [id, onSubmitPaidOff, loadData]
  );

  const handleApproval = async () => {
    if (id) {
      await onApproval(id, showApprovalConfirmation === "approve");
      setShowApprovalConfirmation(null);
      await loadData(id);
    }
  };

  const handleDelete = async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.invoice"),
          })
        );

        navigate(`/invoice`);
      });
    }
  };

  const handleUpdateRecurringSetting = async (
    data: InvoiceRecurringSettingFormData
  ) => {
    if (id) {
      const normalizeData: InvoiceRecurringSettingPayloadType = {
        parent_invoice_id: id,
        number: data.repeat_number,
        unit: data.repeat_unit,
        end_repeat: data.end_repeat,
        is_forever: data.end_repeat === "REPEAT_FOREVER",
        end_after: data.end_after,
        end_on: moment(data.end_on).format("YYYY-MM-DD HH:mm:ss"),
      };

      await onUpdateRecurringSetting(id, normalizeData);
      setShowRecurringSettingModal(false);
      await loadData(id);
    }
  };

  const handleDeleteRecurringSetting = async () => {
    if (id && detailData?.recurring_setting_id !== undefined) {
      await onDeleteRecurringSetting(id);

      setShowDeleteRecurringModal(false);

      await loadData(id);
    }
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <VuiPageTitle
            title={
              <Title level={4} style={{ fontWeight: 500, marginBottom: 0 }}>
                {title}
                <Text style={{ fontWeight: 800 }}>
                  <Space size="small" align="center">
                    {detailData?.name || ""}
                    {hasRecurringSetting && (
                      <RetweetOutlined style={{ color: "#BFBFBF" }} />
                    )}
                    {googleWorkspaceGenerated && (
                      <GoogleIcon
                        width={20}
                        height={20}
                        viewBox="0 0 24 24"
                        color="#BFBFBF"
                        style={{ verticalAlign: "middle" }}
                      />
                    )}
                  </Space>
                </Text>
              </Title>
            }
            onBackLink="/invoice"
            rightTitleContent={[
              <Tag
                color={_.get(
                  INVOICE_STATUS_COLOR,
                  detailData?.status?.label || "",
                  "blue"
                )}
              >
                {detailData?.status?.label}
              </Tag>,
            ]}
          >
            {detailData?.status?.label === "Waiting For Approval" && (
              <>
                {detailData.can_approve && (
                  <Button
                    size="large"
                    type="primary"
                    ghost
                    onClick={() => setShowApprovalConfirmation("approve")}
                  >
                    Approve
                  </Button>
                )}
                {detailData.can_approve && (
                  <Button
                    size="large"
                    type="primary"
                    danger
                    ghost
                    onClick={() => setShowApprovalConfirmation("reject")}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}

            {["Sent", "Due", "Paid"].includes(
              detailData?.status?.label || ""
            ) && (
                <Button
                  size="large"
                  type="primary"
                  ghost
                  icon={<HistoryOutlined />}
                  onClick={() => {
                    setShowHistoryModal(true);
                  }}
                />
              )}

            {detailData?.status?.label === "Waiting For Approval" ? (
              detailData?.can_update && !detailData?.gw_customer_id ? (
                <Button
                  size="large"
                  type="primary"
                  ghost
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                />
              ) : null
            ) : detailData?.can_download ? (
              <Button
                size="large"
                type="primary"
                ghost
                icon={<DownloadOutlined />}
                loading={downloadLoading}
                disabled={downloadLoading}
                onClick={handleDownload}
              />
            ) : null}

            {detailData?.status?.label === "Approved" &&
              detailData?.can_send && (
                <>
                  <VuiAccessible access="invoice.send">
                    <VuiButton
                      title={t("common.button.sendItem", {
                        item: t("common.text.invoice"),
                      })}
                      buttonProps={{
                        onClick: () => {
                          setShowSendForm(true);
                        },
                      }}
                    />
                  </VuiAccessible>
                </>
              )}

            {(detailData?.status?.label === "Sent" ||
              detailData?.status?.label === "Due") && (
                <>
                  <VuiButton
                    title={t("common.button.paymentLink")}
                    buttonProps={{
                      onClick: () => {
                        copy(`${ENV.getPaymentUrl()}/invoice/${detailData?.serial}`).then(() => {
                          openNotification(
                            "success",
                            t("common.text.copiedToClipboard")
                          );
                        });
                      },
                      icon: <CopyOutlined />,
                      ghost: true,
                    }}
                  />

                  <VuiAccessible access="invoice.sendReminder">
                    {detailData?.can_send_reminder && canSendReminder && (
                      <VuiButton
                        title={t("common.button.sendReminder")}
                        buttonProps={{
                          onClick: () => {
                            setShowSendReminderForm(true);
                          },
                          type: "primary",
                          ghost: true,
                        }}
                      />
                    )}
                  </VuiAccessible>
                </>
              )
            }

            {(detailData?.status?.label === "Sent" ||
              detailData?.status?.label === "Due" ||
              detailData?.status?.label === 'Waiting For Confirmation') && (
                <VuiAccessible access="invoice.paidOff">
                  {detailData?.can_paid_off && (
                    <VuiButton
                      title={t("common.button.receivePayment")}
                      buttonProps={{
                        onClick: () => {
                          setShowPaidOffConfirmation(true);
                          if (!defaultPay) {
                            loadDefaultPay({
                              type: "PAY_TO",
                              search: "10002",
                              per_page: 1,
                            }).catch();
                          }
                        },
                      }}
                    />
                  )}
                </VuiAccessible>
              )}

            {(detailData?.status?.label === "Paid" ||
              detailData?.status?.label === "Waiting For Confirmation") && (
                <VuiButton
                  title={t("common.button.viewPayment")}
                  buttonProps={{
                    onClick: () => {
                      setShowPaymentModal(true);
                    },
                  }}
                />
              )}
          </VuiPageTitle>
        </Col>
      </Row>

      <Spin size="large" spinning={isOnFetchingDetailData}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card>
              <Row gutter={[48, 16]}>
                <Col xs={24} md={8}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24}>
                      <VuiFormItemView
                        label={t("common.form.client.label")}
                        prefix={
                          detailData?.customer?.type?.label !== "None"
                            ? detailData?.customer?.type?.label + " "
                            : ""
                        }
                        value={detailData?.customer?.name}
                      />
                    </Col>
                    <Col xs={24}>
                      <VuiFormItemView
                        label={t("common.form.billingAddress.label")}
                        value={detailData?.billing_address}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={8}>
                  <Row gutter={[32, 16]}>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.form.invoiceDate.label")}
                        value={moment(detailData?.date).format("DD MMM YYYY")}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.form.idProject.label")}
                        value={detailData?.project?.code}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.form.projectManager.label")}
                        value={
                          detailData?.project_manager
                            ? `${detailData.project_manager.name} (${detailData.project_manager.initial})`
                            : ""
                        }
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.form.top.label")}
                        value={`${detailData?.tempo} Day(s)`}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.form.withholdingPph.label")}
                        value={
                          detailData?.is_withholding_pph
                            ? t("common.text.yes")
                            : t("common.text.no")
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} md={8}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={10}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.sendType.label")}
                            value={detailData?.sent_type.label}
                          />
                        </Col>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.template.label")}
                            value={detailData?.template?.label}
                          />
                        </Col>
                        {!!detailData?.sent_date && (
                          <Col xs={24}>
                            <VuiFormItemView
                              label={t("common.form.sendDate.label")}
                              value={moment(detailData?.sent_date).format(
                                "D MMM YYYY | HH:mm"
                              )}
                            />
                          </Col>
                        )}
                      </Row>
                    </Col>
                    <Col xs={24} md={14}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.sentToEmails.label")}
                            value={invoiceContactEmails}
                          />
                        </Col>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.sentToCcEmails.label")}
                            value={invoiceContactCcEmails}
                          />
                        </Col>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.sentToWhatsapp.label")}
                            value={invoiceContactWhatsapp}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24}>
            <Table
              rowKey={"id"}
              columns={invoiceProductColumns}
              dataSource={detailData?.invoice_products || []}
              loading={isOnFetchingDetailData}
              pagination={false}
              size="small"
            />
          </Col>
          <Col xs={24}>
            <div className="text-align-right mt24">
              <div>
                <Row justify="end">
                  <Col
                    xs={24}
                    lg={8}
                    style={{
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <table className="table-summary type-one">
                      <tbody>
                        <tr>
                          <td style={{ minWidth: 100 }}>
                            {t("common.text.subtotal")}
                          </td>
                          <td style={{ textAlign: "end" }}>
                            <VuiNumberFormat value={subtotal} prefix={"Rp "} />
                          </td>
                        </tr>
                        {!!hasPPN && (
                          <tr>
                            <td>{t("common.text.ppn")}</td>
                            <td>
                              <VuiNumberFormat
                                className={
                                  "display-flex justify-content-between"
                                }
                                value={ppn}
                                prefix={"Rp "}
                              />
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>{t("common.text.total")}</b>
                          </td>
                          <td>
                            <b>
                              <VuiNumberFormat value={total} prefix={"Rp "} />
                            </b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>

          <Modal
            width={800}
            open={showRecurringSettingModal}
            onCancel={() => setShowRecurringSettingModal(false)}
            title={t("common.text.recurringSetting")}
            footer={
              <>
                <Button
                  htmlType="button"
                  onClick={() => setShowRecurringSettingModal(false)}
                >
                  {t("common.button.cancel")}
                </Button>
                <Button
                  onClick={form.submit}
                  type="primary"
                  loading={recurringSettingLoading}
                >
                  {t("common.button.edit")}
                </Button>
              </>
            }
          >
            <Spin size="large" spinning={isOnFetchingDataDefaultPay}>
              <Form
                layout="vertical"
                form={form}
                onFinish={handleUpdateRecurringSetting}
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="repeat_number"
                      label={t("common.form.recurringNumber.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.recurringNumber.label"),
                          }),
                        },
                      ]}
                    >
                      <InputNumber
                        size="large"
                        controls={false}
                        placeholder={t(
                          "common.form.recurringNumber.placeholder"
                        )}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="repeat_unit"
                      initialValue={"MONTH"}
                      label={t("common.form.repeatEvery.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.repeatEvery.label"),
                          }),
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder={t("common.form.repeatEvery.placeholder")}
                        options={[
                          { label: "Month", value: "MONTH" },
                          { label: "Year", value: "YEAR" },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24}>
                    <Form.Item
                      name="end_repeat"
                      label={t("common.form.endAfter.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.endAfter.label"),
                          }),
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        placeholder={t("common.form.endAfter.placeholder")}
                        options={[
                          { label: "Repeat Forever", value: "REPEAT_FOREVER" },
                          { label: "End After", value: "END_AFTER" },
                          { label: "End On", value: "END_ON" },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {repeatUntil === "END_AFTER" && (
                    <Col xs={24} md={24}>
                      <Form.Item
                        name="end_after"
                        label={t("common.form.endAfter.endAfterGenerate.label")}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: t(
                                "common.form.endAfter.endAfterGenerate.label"
                              ),
                            }),
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          min={1}
                          controls={false}
                          placeholder={t(
                            "common.form.endAfter.endAfterGenerate.placeholder"
                          )}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  {repeatUntil === "END_ON" && (
                    <Col xs={24} md={24}>
                      <Form.Item
                        name="end_on"
                        label={t("common.form.endAfter.endOn.label")}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: t("common.form.endAfter.endOn.label"),
                            }),
                          },
                        ]}
                      >
                        <VuiDatePicker size="large" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Form>
            </Spin>
          </Modal>

          {detailData?.recurring_setting_id ? (
            <Col xs={10}>
              {detailData?.recurring_setting?.parent_invoice_id == id ? (
                <Card
                  title={
                    <Space size="middle">
                      {t("common.text.recurringSetting")}
                      <Tag
                        color={
                          detailData?.recurring_setting?.status === "FINISHED"
                            ? "green"
                            : detailData?.recurring_setting?.status ===
                              "INACTIVE"
                              ? "gray"
                              : "blue"
                        }
                      >
                        {t(
                          `common.text.${_.camelCase(
                            detailData?.recurring_setting?.status ?? ""
                          )}`
                        )}
                      </Tag>
                    </Space>
                  }
                  extra={
                    !["FINISHED", "INACTIVE"].includes(
                      detailData?.recurring_setting?.status || ""
                    ) && (
                      <Space size="middle">
                        <VuiAccessible access="invoice.update">
                          <Text
                            className="cursor-pointer"
                            onClick={() => setShowDeleteRecurringModal(true)}
                            title={t("common.text.cancel")}
                            type="secondary"
                          >
                            {t("common.button.cancel")}
                          </Text>
                        </VuiAccessible>
                      </Space>
                    )
                  }
                >
                  <Row gutter={[16, 16]}>
                    {!["FINISHED", "INACTIVE"].includes(
                      detailData?.recurring_setting?.status || ""
                    ) && (
                        <Col xs={24} md={12}>
                          <VuiFormItemView
                            label={t("common.text.nextTransactionDate")}
                            value={moment
                              .unix(
                                detailData?.recurring_setting
                                  ?.next_transaction_date ?? 0
                              )
                              .format("DD MMM YYYY")}
                          />
                        </Col>
                      )}
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.text.repeatEvery")}
                        value={`${detailData?.recurring_setting?.number ?? ""
                          } ${uppercaseToTitleCase(
                            detailData?.recurring_setting?.unit ?? ""
                          )}`}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.text.ends")}
                        value={
                          detailData?.recurring_setting?.is_forever
                            ? t("common.text.never")
                            : detailData?.recurring_setting?.end_after
                              ? `${t("common.text.after")} ${detailData?.recurring_setting?.end_after
                              } ${t("common.text.occurrences")}`
                              : `On ${moment
                                .unix(
                                  Number(detailData?.recurring_setting?.end_on)
                                )
                                .format("DD MMM YYYY")}`
                        }
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <VuiFormItemView
                        label={t("common.text.needApproval")}
                        value={t(
                          detailData?.recurring_setting?.with_approval
                            ? "common.text.yes"
                            : "common.text.no"
                        )}
                      />
                    </Col>
                  </Row>
                </Card>
              ) : (
                <Card>
                  <Text>
                    {t(
                      "common.text.thisInvoiceIsGeneratedFromRecurringSettingOnInvoice"
                    )}{" "}
                    <Link
                      className="link"
                      to={`/invoice/${detailData?.recurring_setting?.parent_invoice_id}`}
                    >
                      {detailData?.recurring_setting?.parent_invoice?.name}
                    </Link>
                  </Text>
                </Card>
              )}
            </Col>
          ) : null}

          <Col
            xs={24}
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <VuiAccessible access="invoice.destroy">
              {detailData?.can_delete && (
                <Text
                  className="cursor-pointer"
                  onClick={() => setShowDeleteModal(true)}
                  title={t("common.text.delete")}
                  type="secondary"
                >
                  {t("common.text.delete")}
                </Text>
              )}
            </VuiAccessible>

            <Text type="secondary">
              {t("common.text.createdByOn", {
                name: detailData?.creator?.name,
                date: moment(detailData?.created_at).format(
                  "DD MMM YYYY, HH:mm"
                ),
              })}
            </Text>
          </Col>
        </Row>
      </Spin>

      <Modal
        width={1300}
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        title={t("common.text.payment")}
        footer={
          <>
            <Button
              htmlType="button"
              onClick={() => setShowPaymentModal(false)}
            >
              {t("common.button.cancel")}
            </Button>
          </>
        }
      >
        <Table
          rowKey={"id"}
          columns={invoicePaymentColumns}
          dataSource={detailData?.invoice_payments || []}
          loading={isOnFetchingDetailData}
          pagination={false}
          size="small"
        />
      </Modal>
      <VuiModalConfirmation
        show={showConfirmPaymentModal}
        isLoading={confirmPaymentLoading}
        onSubmit={async () => {
          if (selectedPaymentId == null) return;
          setConfirmPaymentLoading(true);
          await InvoiceRepository.confirmPayment(selectedPaymentId)
            .then(() => {
              openNotification("success", t("notification.success.confirmItem", { item: t("common.text.payment") }));
            })
            .catch((e: AxiosError) => {
              handleBackendError(e);
            })
            .finally(async () => {
              setConfirmPaymentLoading(false);
              setShowConfirmPaymentModal(false);
              setShowPaymentModal(false);
              setSelectedPaymentId(null);
              if (id) await loadData(id);
            });
        }}
        onCancel={() => {
          setShowConfirmPaymentModal(false);
          setSelectedPaymentId(null);
        }}
        isSubmitDanger={false}
        submitBtnLabel={t("common.button.confirm")}
        title={"Confirm Payment"}
        subtitle={"Are you sure you want to confirm this payment?"}
      />
      <VuiModalConfirmation
        show={showRejectPaymentModal}
        isLoading={rejectPaymentLoading}
        onSubmit={async () => {
          if (selectedPaymentId == null) return;
          setRejectPaymentLoading(true);
          await InvoiceRepository.rejectPayment(selectedPaymentId)
            .then(() => {
              openNotification("success", t("notification.success.rejectPayment"));
            })
            .catch((e: AxiosError) => {
              handleBackendError(e);
            })
            .finally(async () => {
              setRejectPaymentLoading(false);
              setShowRejectPaymentModal(false);
              setShowPaymentModal(false);
              setSelectedPaymentId(null);
              if (id) await loadData(id);
            });
        }}
        onCancel={() => {
          setShowRejectPaymentModal(false);
          setSelectedPaymentId(null);
        }}
        isSubmitDanger={true}
        submitBtnLabel={t("common.button.reject")}
        title={"Reject Payment"}
        subtitle={"Are you sure you want to reject this payment?"}
      />
      <Modal
        width={800}
        open={showPaidOffConfirmation}
        onCancel={() => setShowPaidOffConfirmation(false)}
        title={t("common.text.receivePayment")}
        footer={
          <>
            <Button
              htmlType="button"
              onClick={() => setShowPaidOffConfirmation(false)}
            >
              {t("common.button.cancel")}
            </Button>
            <Button
              onClick={() => form.submit()}
              type="primary"
              loading={paidOffLoading}
            >
              {t("common.button.submit")}
            </Button>
          </>
        }
      >
        <Spin size="large" spinning={isOnFetchingDataDefaultPay}>
          <Form layout="vertical" form={form} onFinish={handleSubmitPaidOff}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="date"
                  initialValue={moment()}
                  label={t("common.form.paymentDate.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.paymentDate.label"),
                      }),
                    },
                  ]}
                >
                  <VuiDatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="payment_method"
                  initialValue={"BANK_TRANSFER"}
                  label={t("common.form.paymentMethod.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.paymentMethod.label"),
                      }),
                    },
                  ]}
                >
                  <Select
                    size="large"
                    placeholder={t("common.form.paymentMethod.placeholder")}
                    options={[
                      {
                        label: "Cash",
                        value: "CASH",
                      },
                      {
                        label: "Bank Transfer",
                        value: "BANK_TRANSFER",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="pay_coa_id"
                  label={t("common.form.payToAccount.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.payToAccount.label"),
                      }),
                    },
                  ]}
                >
                  <VuiSelect
                    repository={ChartOfAccountRepository}
                    placeholder={t("common.form.payToAccount.placeholder")}
                    repositoryParams={{
                      type: "PAY_TO",
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="amount" label={t("common.form.amount.label")}>
                  <InputNumber
                    disabled
                    size="large"
                    placeholder={t("common.form.amount.placeholder")}
                    formatter={inputNumberThousandSeparator.formatter}
                    parser={inputNumberThousandSeparator.parser}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="withholding_coa_id"
                  label={t("common.form.withholdingAccount.label")}
                >
                  <VuiSelect
                    repository={ChartOfAccountRepository}
                    placeholder={t(
                      "common.form.withholdingAccount.placeholder"
                    )}
                    repositoryParams={{
                      type: "WITHHOLDING",
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="withholding_amount"
                  label={t("common.form.withholdingAmount.label")}
                  extra={
                    withholdingType === "PERCENTAGE" && (
                      <VuiNumberFormat
                        value={withholdingRealAmount}
                        prefix="Rp "
                      />
                    )
                  }
                  rules={[
                    {
                      validator: async (_, value) => {
                        const realAmount =
                          withholdingType === "PERCENTAGE"
                            ? (Number(value) / 100) * total
                            : value;

                        if (realAmount > total) {
                          return Promise.reject(
                            t("validation.withholdingAmountMoreThanTotal")
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                    {
                      required: withHoldingAccountSelected,
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={withholdingType === "PERCENTAGE" ? 100 : total}
                    size="large"
                    placeholder={t("common.form.amount.placeholder")}
                    addonBefore={
                      <Select
                        value={withholdingType}
                        onChange={(value) => {
                          form.setFieldValue("withholding_amount", 0);
                          setWithholdingType(value);
                          setWithholdingRealAmount(0);
                        }}
                        defaultValue="PERCENTAGE"
                      >
                        <Option value="PERCENTAGE">%</Option>
                        <Option value="AMOUNT">Rp</Option>
                      </Select>
                    }
                    formatter={inputNumberThousandSeparator.formatter}
                    onChange={(value) => {
                      form.setFieldsValue({
                        receive_amount:
                          withholdingType === "PERCENTAGE"
                            ? total - (Number(value) / 100) * total
                            : total - Number(value),
                      });
                      if (withholdingType === "PERCENTAGE") {
                        setWithholdingRealAmount((Number(value) / 100) * total);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="receive_amount"
                  label={t("common.form.receiveAmount.label")}
                >
                  <InputNumber
                    disabled
                    size="large"
                    placeholder={t("common.form.receiveAmount.placeholder")}
                    formatter={inputNumberThousandSeparator.formatter}
                    parser={inputNumberThousandSeparator.parser}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="attachment_id">
                  <VuiUploadImage
                    label={t("common.text.attachment")}
                    accept="application/pdf"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
      <Modal
        width={800}
        open={showHistoryModal}
        onCancel={() => setShowHistoryModal(false)}
        title={t("common.text.sendAndReminderHistory")}
        footer={
          <>
            <Button
              htmlType="button"
              onClick={() => setShowHistoryModal(false)}
            >
              {t("common.button.cancel")}
            </Button>
          </>
        }
      >
        <Table
          rowKey={"id"}
          columns={notificationHistoryColumns}
          dataSource={notificationHistoryData}
          loading={isOnFetchingDetailData}
          size="small"
        />
      </Modal>
      <ModalUpload
        title={
          <>
            <Title
              level={5}
              style={{
                color: " #979DAD",
                fontWeight: 400,
              }}
            >
              Upload Invoice
              <br />
              <Title level={4}>{detailData?.project?.code}</Title>
            </Title>
          </>
        }
        show={showUploadForm}
        onClose={() => setShowUploadForm(false)}
        onSubmit={handleUpload}
        submitLoading={uploadLoading}
      />
      <VuiModalConfirmation
        show={showSendForm}
        isLoading={sendLoading}
        onSubmit={handleSend}
        onCancel={() => setShowSendForm(false)}
        isSubmitDanger={false}
        submitBtnLabel={t("common.button.send")}
        title=""
        subtitle={t("modal.invoiceSendConfirmationItem.title", {
          value:
            haveEmailContact && haveWhatsappContact
              ? t("modal.invoiceSendConfirmationItem.emailAndWhatsapp", {
                emails: invoiceContactEmails,
                numbers: invoiceContactWhatsapp,
              })
              : haveEmailContact
                ? t("modal.invoiceSendConfirmationItem.email", {
                  emails: invoiceContactEmails,
                })
                : t("modal.invoiceSendConfirmationItem.whatsapp", {
                  numbers: invoiceContactWhatsapp,
                }),
        })}
      />
      <VuiModalConfirmation
        show={showSendReminderForm}
        isLoading={sendReminderLoading}
        onSubmit={handleSendReminder}
        onCancel={() => setShowSendReminderForm(false)}
        isSubmitDanger={false}
        submitBtnLabel={t("common.button.send")}
        title=""
        subtitle={t("modal.invoiceSendReminderConfirmation.title", {
          value:
            haveEmailContact && haveWhatsappContact
              ? t("modal.invoiceSendReminderConfirmation.emailAndWhatsapp", {
                emails: invoiceContactEmails,
                numbers: invoiceContactWhatsapp,
              })
              : haveEmailContact
                ? t("modal.invoiceSendReminderConfirmation.email", {
                  emails: invoiceContactEmails,
                })
                : t("modal.invoiceSendReminderConfirmation.whatsapp", {
                  numbers: invoiceContactWhatsapp,
                }),
        })}
      />
      <VuiModalConfirmation
        show={!!showApprovalConfirmation}
        isLoading={isApprovalSubmitting}
        onSubmit={handleApproval}
        onCancel={() => setShowApprovalConfirmation(null)}
        isSubmitDanger={false}
        submitBtnLabel={t("common.button.submit")}
        title=""
        subtitle={
          showApprovalConfirmation === "approve"
            ? t("modal.invoiceApproveConfirmation.title")
            : t("modal.invoiceRejectConfirmation.title")
        }
      />
      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
      <VuiModalConfirmation
        show={showDeleteRecurringModal}
        isLoading={recurringSettingLoading}
        title={t("modal.recurringSettingCancel.title")}
        subtitle={t("modal.recurringSettingCancel.subtitle")}
        onSubmit={handleDeleteRecurringSetting}
        onCancel={() => setShowDeleteRecurringModal(false)}
        submitBtnLabel={t("common.button.submit")}
      />
    </>
  );
};

export default InvoiceDetailModule;
